import { useEffect } from 'react';

import { colorForId, initialsOf } from '~/client/lib/format';
import { formatDayLabel } from '~/shared/dates';
import type { Respondent } from '~/shared/types';

import { Icon } from './Icons';
import { Avatar, Chip } from './Primitives';

function partition(respondents: Respondent[], day: string) {
  const yes: Respondent[] = [];
  const maybe: Respondent[] = [];
  const no: Respondent[] = [];
  for (const r of respondents) {
    if (r.yes.includes(day)) yes.push(r);
    else if (r.maybe.includes(day)) maybe.push(r);
    else no.push(r);
  }
  return { yes, maybe, no };
}

interface DayPreviewProps {
  day: string;
  respondents: Respondent[];
}

export function DayPreview({ day, respondents }: DayPreviewProps) {
  const { yes, maybe, no } = partition(respondents, day);
  const total = respondents.length;

  return (
    <div className="day-preview">
      <div className="day-preview-head">
        <span className="day-preview-date">{formatDayLabel(day)}</span>
        <span className="day-preview-count mono">
          {yes.length}
          {maybe.length > 0 ? `+${maybe.length}?` : ''}/{total}
        </span>
      </div>
      {total === 0 ? (
        <div className="day-preview-empty">no replies yet</div>
      ) : (
        <>
          {yes.length > 0 && (
            <div className="day-preview-row">
              <Icon name="check" size={12} />
              <span className="day-preview-names">{yes.map((r) => r.name).join(' · ')}</span>
            </div>
          )}
          {maybe.length > 0 && (
            <div className="day-preview-row day-preview-row-maybe">
              <span className="day-preview-glyph">?</span>
              <span className="day-preview-names">{maybe.map((r) => r.name).join(' · ')}</span>
            </div>
          )}
          {no.length > 0 && (
            <div className="day-preview-row day-preview-row-no">
              <Icon name="x" size={12} />
              <span className="day-preview-names">{no.map((r) => r.name).join(' · ')}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface DayBreakdownProps {
  day: string;
  respondents: Respondent[];
  selfRespondentId?: string;
  onClose: () => void;
}

export function DayBreakdown({ day, respondents, selfRespondentId, onClose }: DayBreakdownProps) {
  const { yes, maybe, no } = partition(respondents, day);
  const total = respondents.length;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const renderRow = (r: Respondent, opts: { dim?: boolean; trailing?: React.ReactNode }) => (
    <div key={r.id} className="respondent-row" style={opts.dim ? { opacity: 0.55 } : undefined}>
      <Avatar initials={initialsOf(r.name)} color={colorForId(r.id)} size={28} />
      <span className="name">
        {r.name}
        {r.id === selfRespondentId && (
          <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
            (you)
          </span>
        )}
      </span>
      {opts.trailing}
    </div>
  );

  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="row between" style={{ marginBottom: 12 }}>
          <div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 24,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                margin: 0,
              }}
            >
              {formatDayLabel(day)}
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="close"
          >
            <Icon name="x" size={14} />
          </button>
        </div>

        {total > 0 ? (
          <div className="row row-wrap" style={{ gap: 8, marginBottom: 16 }}>
            <Chip variant={yes.length === total ? 'lime' : 'soft'} dot>
              {yes.length}/{total} in
            </Chip>
            {maybe.length > 0 && <Chip variant="blue">{maybe.length} maybe</Chip>}
          </div>
        ) : (
          <p className="muted" style={{ marginTop: 0, marginBottom: 0, fontSize: 14 }}>
            no replies yet — share the link to get going.
          </p>
        )}

        {yes.length > 0 && (
          <>
            <div className="field-label">in</div>
            <div className="col" style={{ gap: 10, marginTop: 8, marginBottom: 16 }}>
              {yes.map((r) => renderRow(r, { trailing: <Icon name="check" size={14} /> }))}
            </div>
          </>
        )}

        {maybe.length > 0 && (
          <>
            <div className="field-label">maybe</div>
            <div className="col" style={{ gap: 10, marginTop: 8, marginBottom: 16 }}>
              {maybe.map((r) =>
                renderRow(r, {
                  trailing: <span className="day-preview-glyph">?</span>,
                }),
              )}
            </div>
          </>
        )}

        {no.length > 0 && (
          <>
            <div className="field-label">not free</div>
            <div className="col" style={{ gap: 10, marginTop: 8 }}>
              {no.map((r) => renderRow(r, { dim: true }))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
